/**
 * PRODUTO-V1 — Modo Foco.
 *
 * Overlay fullscreen com Pomodoro 25/5. Estado local (sem backend),
 * timer client-side. Entry point: botão "Modo Foco" no HeroFocusCard.
 *
 * UX:
 *   - Bloco grande com título da task selecionada
 *   - Timer mm:ss centralizado, controles Start/Pause/Reset
 *   - "Concluir tarefa" → PATCH status=done + sai do overlay
 *   - Counter de pomodoros completados na sessão
 *   - Chime via WebAudio API (oscillator simples) ao final de cada ciclo
 *   - Esc fecha overlay (com confirm se timer rolando)
 *
 * Telemetria via executionTrace.js (events: focus.session_started,
 * focus.pomodoro_completed, focus.task_completed_in_focus,
 * focus.session_ended).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, X, Check, Coffee, Target } from "lucide-react";
import { toast } from "sonner";
import trace from "@/services/executionTrace";

const POMODORO_MIN = 25;
const BREAK_MIN = 5;

function formatMMSS(totalSec) {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.8);
    osc.stop(ctx.currentTime + 0.9);
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {/* */}
}

export default function FocusMode({ task, isOpen, onClose, onCompleteTask }) {
  const [phase, setPhase] = useState("focus"); // focus | break
  const [secondsLeft, setSecondsLeft] = useState(POMODORO_MIN * 60);
  const [running, setRunning] = useState(false);
  const [pomodorosDone, setPomodorosDone] = useState(0);
  const startedAtRef = useRef(null);
  const tickRef = useRef(null);

  // Reset state on open.
  useEffect(() => {
    if (isOpen) {
      setPhase("focus");
      setSecondsLeft(POMODORO_MIN * 60);
      setRunning(false);
      setPomodorosDone(0);
      startedAtRef.current = Date.now();
      try {
        trace.record("focus.session_started", {
          has_task: !!task,
          urgency: task?.urgency || task?.priority || "unknown",
        });
      } catch {/* */}
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick loop.
  useEffect(() => {
    if (!isOpen || !running) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          // Switch phase.
          playChime();
          if (phase === "focus") {
            try {
              trace.record("focus.pomodoro_completed", {
                count: pomodorosDone + 1,
              });
            } catch {/* */}
            setPomodorosDone((p) => p + 1);
            setPhase("break");
            toast.success("Pomodoro completo! Pausa de 5 min.");
            return BREAK_MIN * 60;
          }
          setPhase("focus");
          toast.info("Hora de voltar ao foco.");
          return POMODORO_MIN * 60;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [isOpen, running, phase, pomodorosDone]);

  const handleClose = useCallback(() => {
    if (running && !window.confirm("Sair do Modo Foco? O timer será descartado.")) return;
    try {
      const durationSec = startedAtRef.current
        ? Math.floor((Date.now() - startedAtRef.current) / 1000)
        : 0;
      trace.record("focus.session_ended", {
        pomodoros: pomodorosDone,
        duration_sec: durationSec,
      });
    } catch {/* */}
    setRunning(false);
    onClose?.();
  }, [running, pomodorosDone, onClose]);

  const handleToggle = useCallback(() => {
    setRunning((r) => !r);
  }, []);

  const handleReset = useCallback(() => {
    setRunning(false);
    setSecondsLeft(phase === "focus" ? POMODORO_MIN * 60 : BREAK_MIN * 60);
  }, [phase]);

  const handleCompleteTask = useCallback(async () => {
    if (!task) {
      handleClose();
      return;
    }
    try {
      trace.record("focus.task_completed_in_focus", {
        pomodoros: pomodorosDone,
      });
    } catch {/* */}
    try { await onCompleteTask?.(task); } catch {/* */}
    handleClose();
  }, [task, pomodorosDone, onCompleteTask, handleClose]);

  // Esc handler.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === " " && e.target?.tagName !== "INPUT") {
        e.preventDefault();
        handleToggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose, handleToggle]);

  const isBreak = phase === "break";
  const phaseLabel = isBreak ? "Pausa" : "Foco";
  const phaseColor = isBreak ? "#22c55e" : "#06b6d4";
  const totalForPhase = isBreak ? BREAK_MIN * 60 : POMODORO_MIN * 60;
  const progress = 1 - secondsLeft / totalForPhase;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-[#020617]/95 backdrop-blur-xl flex flex-col items-center justify-center p-6"
          data-testid="focus-mode-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Modo Foco"
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-6 right-6 p-2 rounded-full text-slate-500 hover:text-white hover:bg-white/[0.05] transition-colors"
            aria-label="Fechar"
            data-testid="focus-mode-close"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="max-w-2xl w-full text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              {isBreak ? (
                <Coffee className="w-4 h-4 text-emerald-400" />
              ) : (
                <Target className="w-4 h-4 text-cyan-400" />
              )}
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium"
                style={{ color: phaseColor }}
              >
                {phaseLabel}
              </p>
            </div>

            <h2
              className="text-2xl md:text-3xl font-bold text-white mb-10 truncate"
              data-testid="focus-mode-task-title"
              title={task?.title || ""}
            >
              {task?.title || "Sem tarefa selecionada"}
            </h2>

            {/* Circular progress */}
            <div className="relative w-72 h-72 mx-auto mb-10">
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50" cy="50" r="46"
                  fill="none"
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth="2"
                />
                <circle
                  cx="50" cy="50" r="46"
                  fill="none"
                  stroke={phaseColor}
                  strokeWidth="2"
                  strokeDasharray={`${2 * Math.PI * 46}`}
                  strokeDashoffset={`${2 * Math.PI * 46 * (1 - progress)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="text-6xl md:text-7xl font-bold text-white tabular-nums tracking-tight"
                  data-testid="focus-mode-timer"
                >
                  {formatMMSS(secondsLeft)}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 mb-6">
              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleToggle}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-base font-medium border transition-all"
                style={{
                  backgroundColor: `${phaseColor}1a`,
                  borderColor: `${phaseColor}40`,
                  color: phaseColor,
                }}
                data-testid="focus-mode-toggle"
              >
                {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {running ? "Pausar" : "Começar"}
              </motion.button>
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                data-testid="focus-mode-reset"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>

            {task && (
              <button
                type="button"
                onClick={handleCompleteTask}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/15 transition-colors"
                data-testid="focus-mode-complete-task"
              >
                <Check className="w-4 h-4" />
                Concluir tarefa
              </button>
            )}

            <p className="mt-8 text-xs text-slate-600">
              {pomodorosDone} pomodoro{pomodorosDone === 1 ? "" : "s"} nesta sessão · Esc fecha · Espaço pausa
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
